import { useState } from "react";
import type { FormEvent } from "react";
import { ArrowLeft, CircleAlert, LockKeyhole } from "lucide-react";
import { Bilingual } from "@tradvera/design-system";
import { normalizePinDigits } from "./access";

type AdminRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

export function AdminSessionReauth({ request, onUnlocked }: { request: AdminRequest; onUnlocked: (payload: { adminSession: string; forcePinChange: boolean }) => void }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!/^\d{4}$/.test(pin)) return setMessage("PIN_FORMAT_INVALID");
    setBusy(true); setMessage("");
    try { onUnlocked(await request("/unlock", { method: "POST", body: JSON.stringify({ pin }) })); }
    catch (cause) { setMessage(adminReauthErrorCode(cause)); setPin(""); }
    finally { setBusy(false); }
  };

  const error = message ? adminReauthErrorCopy(message) : null;
  return <div className="admin-reauth-backdrop"><section className="admin-reauth-card" role="dialog" aria-modal="true" aria-label="إعادة فتح جلسة الإدارة / Reopen admin session">
    <LockKeyhole/><small>SESSION PAUSED · DATA PRESERVED</small>
    <h2><Bilingual ar="الجلسة توقفت لحماية البيانات" en="Session paused to protect your data" /></h2>
    <p><Bilingual ar="اكتب رقم الإدارة مرة أخرى. تبقى المسودات الآمنة، أما تأكيد الإجراء الحساس فيُعاد من بدايته بعد الفتح."
      en="Enter the admin PIN again. Safe drafts remain, while sensitive-action confirmation restarts after unlock." /></p>
    <form className="admin-pin-form" onSubmit={submit}>
      <label htmlFor="admin-session-reauth-pin"><Bilingual ar="رقم الإدارة للتحقق الحديث" en="Admin PIN for fresh verification" /></label>
      <input id="admin-session-reauth-pin" type="password" autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={4} value={pin} onChange={(event) => setPin(normalizePinDigits(event.target.value))} placeholder="••••" aria-label="رقم الإدارة للتحقق الحديث / Admin PIN for fresh verification"/>
      {error && <p className="gate-error"><CircleAlert/><Bilingual {...error} /></p>}
      <button disabled={busy}><Bilingual ar={busy ? "جارٍ إعادة الفتح…" : "تحقق وافتح الجلسة"}
        en={busy ? "Reopening session…" : "Verify and reopen"} /><ArrowLeft/></button>
    </form>
  </section></div>;
}

function adminReauthErrorCode(cause: unknown) {
  return cause instanceof Error ? cause.message : "REQUEST_FAILED";
}

export function adminReauthErrorCopy(code: string) {
  return ({
    PIN_INVALID: { ar: "الرقم السري غير صحيح.", en: "The admin PIN is incorrect." },
    PIN_LOCKED: { ar: "تم قفل المحاولات 15 دقيقة لحماية الحساب.", en: "Attempts are locked for 15 minutes to protect the account." },
    PIN_FORMAT_INVALID: { ar: "الرقم السري يجب أن يكون أربع خانات.", en: "The admin PIN must contain four digits." },
    REQUEST_FAILED: { ar: "تعذر إعادة فتح الجلسة الآن.", en: "The session could not be reopened now." },
  } as Record<string, { ar: string; en: string }>)[code]
    ?? { ar: code.replaceAll("_", " "), en: code.replaceAll("_", " ") };
}
